// Mock data for the Fatigue Management dashboard.
//
// The four proprietary metrics — FED, LRM, CDI, PDC — and the three-layer
// stakeholder model (Command / Operations / Detail) are specified in
// "PEAK4 Fatigue Management Dashboard.pptx". No backend exists for these yet,
// so this file provides representative, deck-aligned figures so the UI can be
// reviewed end-to-end. Swap these for TanStack Query hooks once the algorithm
// engine ships its endpoints.

export type RiskTier = "green" | "amber" | "red";

export type TrendDirection = "up" | "down" | "stable";

export interface HeadlineMetric {
  key: "fed" | "lrm" | "cdi" | "pdc";
  code: string;
  name: string;
  question: string;
  value: number;
  unit: string;
  trend: TrendDirection;
  trendLabel: string;
  // 0–100 fill used by the metric ring; not always equal to `value`.
  fill: number;
  goodDirection: "up" | "down";
  audience: string;
}

export interface TeamWeek {
  team: string;
  shift: string;
  days: RiskTier[]; // Mon..Sun
  lrmRate: number;
  tier: RiskTier;
}

export interface SeriesPoint {
  [key: string]: string | number;
  day: string;
  fed: number;
  lrm: number;
  cdi: number;
}

// ── Command view (C-suite & board) ──────────────────────────────────────────

export const workforceReadinessIndex = {
  value: 62,
  target: 75,
  delta: -7,
  tier: "amber" as RiskTier,
  label: "Moderate risk",
};

export const headlineMetrics: HeadlineMetric[] = [
  {
    key: "fed",
    code: "FED",
    name: "Fatigue Exposure Days",
    question: "How often are people starting work already fatigued?",
    value: 84,
    unit: "per 1,000 workdays",
    trend: "up",
    trendLabel: "+18%",
    fill: 64,
    goodDirection: "down",
    audience: "Board / safety",
  },
  {
    key: "lrm",
    code: "LRM",
    name: "Load–Readiness Misalignment",
    question: "Was the work demand appropriate for their physical state?",
    value: 23,
    unit: "% of shifts misaligned",
    trend: "up",
    trendLabel: "+8%",
    fill: 46,
    goodDirection: "down",
    audience: "Line managers",
  },
  {
    key: "cdi",
    code: "CDI",
    name: "Circadian Disruption Index",
    question: "Is the schedule itself disrupting sleep and recovery?",
    value: 41,
    unit: "CDI score (0–100)",
    trend: "stable",
    trendLabel: "stable",
    fill: 41,
    goodDirection: "down",
    audience: "HR / operations",
  },
  {
    key: "pdc",
    code: "PDC",
    name: "Physiological Deviation Cluster",
    question: "Is the body signalling something is physiologically wrong?",
    value: 12,
    unit: "% workforce flagged",
    trend: "down",
    trendLabel: "−3%",
    fill: 24,
    goodDirection: "down",
    audience: "Occupational health",
  },
];

export const riskTierSplit: { tier: RiskTier; label: string; count: number }[] = [
  { tier: "green", label: "Ready", count: 142 },
  { tier: "amber", label: "Monitor", count: 81 },
  { tier: "red", label: "Act now", count: 24 },
];

export const priorityAlert = {
  severity: "red" as RiskTier,
  title: "Site B — night shift in sustained critical fatigue",
  body:
    "4 consecutive days of high Load–Readiness Misalignment. FED rate 61% this week vs 39% baseline. CDI 58 — consistent with circadian disruption from a sustained night pattern. Schedule review recommended.",
};

// ── Operations view (HR & line managers) ────────────────────────────────────

export const teamStatus: TeamWeek[] = [
  {
    team: "Site B",
    shift: "Night",
    days: ["amber", "amber", "red", "red", "red", "red", "amber"],
    lrmRate: 61,
    tier: "red",
  },
  {
    team: "Factory Line 1",
    shift: "Rotating",
    days: ["green", "amber", "amber", "amber", "red", "amber", "green"],
    lrmRate: 43,
    tier: "amber",
  },
  {
    team: "Civil Eng crew",
    shift: "Day",
    days: ["amber", "amber", "amber", "amber", "amber", "green", "green"],
    lrmRate: 36,
    tier: "amber",
  },
  {
    team: "Site A",
    shift: "Day",
    days: ["green", "green", "amber", "green", "green", "green", "green"],
    lrmRate: 14,
    tier: "green",
  },
];

export const operationsAlert =
  "Site B night shift has been in high LRM for 4 consecutive days — schedule review recommended. FED rate is 61% this week vs 39% baseline. CDI score 58 — consistent with circadian disruption from a sustained night pattern.";

export const metricDrivers: { code: string; name: string; drives: string }[] = [
  {
    code: "FED",
    name: "Fatigue Exposure Days",
    drives:
      "Flags which cohorts have most workers starting shifts fatigued. Triggers wellbeing check and Learning Locker sleep content.",
  },
  {
    code: "LRM",
    name: "Load–Readiness Misalignment",
    drives:
      "Powers the traffic-light grid. Identifies which teams have demand–capacity mismatch and on which days.",
  },
  {
    code: "CDI",
    name: "Circadian Disruption Index",
    drives:
      "Shows whether specific shift patterns are driving disruption. Used to evaluate rota change impact over time.",
  },
  {
    code: "PDC",
    name: "Physiological Deviation Cluster",
    drives:
      "Surfaces cohorts with elevated health-signal flags — prompts OH check-in for the affected individuals.",
  },
];

export const trendSeries: SeriesPoint[] = [
  { day: "Wk-12", fed: 39, lrm: 15, cdi: 44 },
  { day: "Wk-10", fed: 44, lrm: 18, cdi: 47 },
  { day: "Wk-8", fed: 52, lrm: 19, cdi: 49 },
  { day: "Wk-6", fed: 58, lrm: 21, cdi: 53 },
  { day: "Wk-4", fed: 67, lrm: 22, cdi: 55 },
  { day: "Wk-2", fed: 74, lrm: 23, cdi: 57 },
  { day: "Now", fed: 84, lrm: 23, cdi: 58 },
];

// ── Detail view (occupational health) ───────────────────────────────────────

export const cohortDetail = {
  cohort: "Site B — night shift",
  window: "30-day metric breakdown",
  metrics: [
    { label: "FED rate", value: "61%", delta: "+22% vs prior", tier: "red" as RiskTier },
    { label: "LRM rate", value: "61%", delta: "+22% vs prior", tier: "red" as RiskTier },
    { label: "CDI score", value: "58", delta: "High disruption", tier: "red" as RiskTier },
    { label: "PDC rate", value: "18%", delta: "+6% vs prior", tier: "amber" as RiskTier },
    { label: "Avg sleep", value: "5.2h", delta: "−0.8h vs prior", tier: "amber" as RiskTier },
    { label: "Avg HRV", value: "42ms", delta: "−11ms vs prior", tier: "red" as RiskTier },
  ],
  // 14-day daily LRM status (most recent on the right).
  lrm14: [
    "green",
    "green",
    "amber",
    "green",
    "amber",
    "amber",
    "amber",
    "red",
    "red",
    "red",
    "red",
    "red",
    "red",
    "amber",
  ] as RiskTier[],
  insight:
    "Recovery below 34% for 8 of 14 days. Strain consistently above 14. Sleep averaging 5.2h against estimated need of 7.8h — 9.4h cumulative debt. Pattern consistent with circadian disruption from a 6-day night-shift block. CDI of 58 confirms a structural scheduling driver.",
};

export const accessControls: { role: string; access: string }[] = [
  {
    role: "OH clinicians",
    access: "Full individual-level data. Named records. All metric histories.",
  },
  { role: "HR managers", access: "Cohort-level only. No individual names. Aggregate trends." },
  { role: "Line managers", access: "Own team cohort data only. No individual identification." },
  {
    role: "Executives",
    access: "Company-level and site-level. No cohort breakdown below 5 workers.",
  },
];

// ── Command view — forecast ─────────────────────────────────────────────────

// 28-day forecast of Red-tier headcount. `actual` runs to "today", then null;
// `forecast` picks up from today with a confidence band (lo/hi).
export interface ForecastPoint {
  [key: string]: string | number | null;
  day: string;
  actual: number | null;
  forecast: number | null;
  lo: number | null;
  hi: number | null;
}

export const redTierForecast: ForecastPoint[] = [
  { day: "−21d", actual: 14, forecast: null, lo: null, hi: null },
  { day: "−14d", actual: 17, forecast: null, lo: null, hi: null },
  { day: "−7d", actual: 21, forecast: null, lo: null, hi: null },
  { day: "Today", actual: 24, forecast: 24, lo: 24, hi: 24 },
  { day: "+7d", actual: null, forecast: 29, lo: 24, hi: 34 },
  { day: "+14d", actual: null, forecast: 33, lo: 25, hi: 41 },
  { day: "+21d", actual: null, forecast: 31, lo: 22, hi: 42 },
  { day: "+28d", actual: null, forecast: 27, lo: 17, hi: 39 },
];

// ── Operations view — roster heatmap, leaderboard, escalations ──────────────

// Predicted fatigue risk (0–100) per team per upcoming shift. Bio-mathematical
// model style — colour-banded so managers can re-roster BEFORE the shift.
export interface RosterRow {
  team: string;
  shift: string;
  scores: number[]; // next 7 days, predicted fatigue risk 0–100
}

export const rosterForecast: RosterRow[] = [
  { team: "Site B", shift: "Night", scores: [72, 78, 84, 88, 81, 69, 55] },
  { team: "Factory Line 1", shift: "Rotating", scores: [38, 52, 61, 58, 70, 49, 33] },
  { team: "Civil Eng crew", shift: "Day", scores: [41, 44, 47, 45, 52, 31, 22] },
  { team: "Logistics", shift: "Mixed", scores: [55, 49, 58, 62, 60, 47, 40] },
  { team: "Site A", shift: "Day", scores: [18, 22, 31, 19, 24, 14, 12] },
];

export const rosterDayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export interface LeaderboardRow {
  label: string;
  value: number;
  tier: RiskTier;
}

// Worst cohorts by composite fatigue risk — ranked, descending.
export const worstCohorts: LeaderboardRow[] = [
  { label: "Site B — Night", value: 84, tier: "red" },
  { label: "Logistics — Mixed", value: 58, tier: "amber" },
  { label: "Factory Line 1", value: 52, tier: "amber" },
  { label: "Civil Eng crew", value: 41, tier: "amber" },
  { label: "Site A — Day", value: 19, tier: "green" },
];

export interface EscalationItem {
  team: string;
  metric: string;
  consecutiveDays: number;
  severity: "LRM-1" | "LRM-2" | "LRM-3";
  recommendedAction: string;
  loop: string; // closed-loop intervention
}

export const escalations: EscalationItem[] = [
  {
    team: "Site B — Night shift",
    metric: "LRM critical",
    consecutiveDays: 4,
    severity: "LRM-3",
    recommendedAction: "Stand down from high-risk tasks · rest mandated",
    loop: "Notify OH · trigger rota review",
  },
  {
    team: "Logistics — Mixed",
    metric: "LRM warning",
    consecutiveDays: 2,
    severity: "LRM-2",
    recommendedAction: "Consider task reallocation · no overtime",
    loop: "Line-manager alert · Learning Locker recovery content",
  },
  {
    team: "Factory Line 1",
    metric: "FED elevated",
    consecutiveDays: 3,
    severity: "LRM-1",
    recommendedAction: "Individual nudge · encourage sleep · note for rota",
    loop: "Learning Locker sleep content",
  },
];

// ── Detail view — individual tracking (OH only, access controlled) ──────────

export interface IndividualSeriesPoint {
  [key: string]: string | number;
  day: string;
  // sleep debt accumulation
  need: number;
  actual: number;
  // baseline-deviation z-scores for PDC signals (positive = worse)
  hrvZ: number;
  rhrZ: number;
  respZ: number;
  skinTempZ: number;
}

export interface Individual {
  id: string;
  alias: string; // pseudonymised — never a real name in the UI demo
  role: string;
  cohort: string;
  fatigueScore: number; // 0–100, higher = more fatigued
  tier: RiskTier;
  consent: "active" | "withdrawn";
  metrics: { fed: number; lrm: number; cdi: number; pdc: number };
  avgSleep: number;
  sleepNeed: number;
  hrv: number;
  series: IndividualSeriesPoint[];
}

const mkSeries = (
  sleepBase: number,
  zBias: number
): IndividualSeriesPoint[] =>
  Array.from({ length: 14 }, (_, i) => ({
    day: `D-${14 - i}`,
    need: 7.8,
    actual: Math.max(4.2, sleepBase - (i % 3 === 0 ? 0.9 : 0.2) + (i / 30)),
    hrvZ: Math.min(3, Math.max(-1, zBias + Math.sin(i / 2) * 0.6 + i * 0.05)),
    rhrZ: Math.min(3, Math.max(-1, zBias * 0.8 + Math.cos(i / 3) * 0.4 + i * 0.04)),
    respZ: Math.min(3, Math.max(-1, zBias * 0.6 + Math.sin(i / 4) * 0.3)),
    skinTempZ: Math.min(3, Math.max(-1, zBias * 0.5 + Math.cos(i / 2) * 0.25)),
  }));

export const individuals: Individual[] = [
  {
    id: "w-7741",
    alias: "Worker 7741",
    role: "Night operative",
    cohort: "Site B — Night",
    fatigueScore: 82,
    tier: "red",
    consent: "active",
    metrics: { fed: 71, lrm: 64, cdi: 58, pdc: 33 },
    avgSleep: 5.2,
    sleepNeed: 7.8,
    hrv: 42,
    series: mkSeries(5.4, 1.4),
  },
  {
    id: "w-3120",
    alias: "Worker 3120",
    role: "Plant operator",
    cohort: "Logistics — Mixed",
    fatigueScore: 57,
    tier: "amber",
    consent: "active",
    metrics: { fed: 48, lrm: 51, cdi: 44, pdc: 18 },
    avgSleep: 6.4,
    sleepNeed: 7.6,
    hrv: 58,
    series: mkSeries(6.6, 0.6),
  },
  {
    id: "w-9056",
    alias: "Worker 9056",
    role: "Civil engineer",
    cohort: "Civil Eng crew",
    fatigueScore: 28,
    tier: "green",
    consent: "active",
    metrics: { fed: 19, lrm: 22, cdi: 31, pdc: 8 },
    avgSleep: 7.5,
    sleepNeed: 7.7,
    hrv: 74,
    series: mkSeries(7.6, -0.2),
  },
];
